/**
 * Locations Redux Slice
 *
 * Manages location selection for portal headers.
 * When a location is selected:
 * - If it has a factory → sets derivedFactoryId for X-Factory-Id header
 * - Sets currentLocationId for X-Location-Id header
 */

import { createSlice, type PayloadAction } from '@spcc/core'
import type { LocationSummary } from '../domain/types'
import { LOCATION_STORAGE_KEY } from '../domain/constants'

export interface LocationsState {
  /** Currently selected location for header */
  currentLocation: LocationSummary | null
  currentLocationId: string | null
  /** Factory documentId derived from the selected location (null if not a factory location) */
  derivedFactoryId: string | null
  /** Available locations for the header dropdown */
  locationsList: LocationSummary[]
  loading: boolean
  error: string | null
}

const initialState: LocationsState = {
  currentLocation: null,
  currentLocationId: null,
  derivedFactoryId: null,
  locationsList: [],
  loading: false,
  error: null,
}

export const locationsSlice = createSlice({
  name: 'locations',
  initialState,
  reducers: {
    /** Fetch locations for the header dropdown */
    fetchLocationsList: (state) => {
      state.loading = true
      state.error = null
    },
    fetchLocationsListSuccess: (state, action: PayloadAction<LocationSummary[]>) => {
      state.locationsList = action.payload
      state.loading = false
    },
    fetchLocationsListFailure: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = action.payload
    },

    /**
     * Select a location (or null for "All Locations" view)
     * Also derives the factoryId if the location belongs to a factory.
     */
    selectLocation: (state, action: PayloadAction<LocationSummary | null>) => {
      const location = action.payload
      state.currentLocation = location
      state.currentLocationId = location?.documentId ?? null
      state.derivedFactoryId = location?.factoryDocumentId ?? null

      if (typeof window !== 'undefined') {
        if (location) {
          localStorage.setItem(LOCATION_STORAGE_KEY, JSON.stringify(location))
        } else {
          localStorage.removeItem(LOCATION_STORAGE_KEY)
        }
      }
    },

    /** Clear location selection */
    clearLocationSelection: (state) => {
      state.currentLocation = null
      state.currentLocationId = null
      state.derivedFactoryId = null

      if (typeof window !== 'undefined') {
        localStorage.removeItem(LOCATION_STORAGE_KEY)
      }
    },

    /** Restore selection from localStorage on app init */
    restoreLocationFromStorage: (state) => {
      if (typeof window === 'undefined') return

      const stored = localStorage.getItem(LOCATION_STORAGE_KEY)
      if (stored) {
        try {
          const location = JSON.parse(stored) as LocationSummary
          state.currentLocation = location
          state.currentLocationId = location.documentId
          state.derivedFactoryId = location.factoryDocumentId ?? null
        } catch {
          localStorage.removeItem(LOCATION_STORAGE_KEY)
        }
      }
    },

    /** Reset all location context */
    resetLocationContext: (state) => {
      if (typeof window !== 'undefined') {
        localStorage.removeItem(LOCATION_STORAGE_KEY)
      }
      state.currentLocation = null
      state.currentLocationId = null
      state.derivedFactoryId = null
      state.locationsList = []
      state.loading = false
      state.error = null
    },
  },
})

export const locationsActions = locationsSlice.actions
export const locationsReducer = locationsSlice.reducer
